import { useState, type FormEvent, type ReactNode } from "react"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { AuthField } from "../AuthField/AuthField"
import { Input } from "../Input/Input"
import { PasswordInput } from "../PasswordInput/PasswordInput"
import { Checkbox } from "../Checkbox/Checkbox"
import { Button } from "../Button/Button"

export interface SignUpFormErrors {
  name?: string
  email?: string
  password?: string
  terms?: string
}

export interface SignUpFormValues {
  name: string
  email: string
  password: string
  termsAccepted: boolean
}

export interface SignUpFormProps {
  heading?: string
  description?: string
  defaultName?: string
  defaultEmail?: string
  defaultPassword?: string
  defaultTermsAccepted?: boolean
  loading?: boolean
  errors?: SignUpFormErrors
  submitLabel?: string
  submittingLabel?: string
  nameLabel?: string
  emailLabel?: string
  passwordLabel?: string
  termsLabel?: ReactNode
  signInPrompt?: ReactNode
  signInLinkSlot?: ReactNode
  captchaSlot?: ReactNode
  onSubmit?: (values: SignUpFormValues) => void
  className?: string
}

export function SignUpForm({
  heading = "Create your account",
  description,
  defaultName = "",
  defaultEmail = "",
  defaultPassword = "",
  defaultTermsAccepted = false,
  loading = false,
  errors,
  submitLabel = "Get Started",
  submittingLabel = "Creating account...",
  nameLabel = "Name",
  emailLabel = "Email",
  passwordLabel = "Password",
  termsLabel,
  signInPrompt,
  signInLinkSlot,
  captchaSlot,
  onSubmit,
  className,
}: SignUpFormProps) {
  const [name, setName] = useState(defaultName)
  const [email, setEmail] = useState(defaultEmail)
  const [password, setPassword] = useState(defaultPassword)
  const [terms, setTerms] = useState(defaultTermsAccepted)

  const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    onSubmit?.({ name, email, password, termsAccepted: terms })
  }

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="text-center mb-5">
        <h1 className="text-page-title text-foreground mb-1">{heading}</h1>
        {description && <p className="text-body text-muted-foreground">{description}</p>}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
        <AuthField label={nameLabel} error={errors?.name}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
            autoComplete="name"
            className="h-9 text-body bg-background border-border"
          />
        </AuthField>

        <AuthField label={emailLabel} error={errors?.email}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="h-9 text-body bg-background border-border"
          />
        </AuthField>

        <AuthField label={passwordLabel} error={errors?.password}>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            className="h-9 text-body bg-background border-border"
          />
        </AuthField>

        {termsLabel && (
          <>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={terms}
                onCheckedChange={(c) => setTerms(c === true)}
                className="mt-0.5"
              />
              <span className="text-body text-muted-foreground leading-relaxed">
                {termsLabel}
              </span>
            </label>
            {errors?.terms && (
              <p className="text-caption text-destructive -mt-2 ml-6">{errors.terms}</p>
            )}
          </>
        )}

        {captchaSlot}

        <Button type="submit" disabled={loading} className="w-full text-label">
          {loading && <Loader2 size={14} className="animate-spin mr-1.5" />}
          {loading ? submittingLabel : submitLabel}
        </Button>
      </form>

      {(signInPrompt || signInLinkSlot) && (
        <p className="text-center text-body text-muted-foreground mt-5">
          {signInPrompt} {signInLinkSlot}
        </p>
      )}
    </div>
  )
}
